/**
 * Funding Radar.
 *
 * Matching is the easy part and it is the part that demos well. The hard part
 * is that there is no machine-readable feed of primary care funding — see
 * PRODUCTION-BUILD.md 9. Opportunities in this system are human-curated
 * records with a named curator and a verified-on date, optionally drafted by
 * a model from a source document but never published without human approval.
 *
 * Two things this module will not do: assert eligibility, and predict success.
 * A match score is a relevance hint.
 */

import { z } from 'zod';

export const OPPORTUNITY_TYPES = [
  'enhanced-service',
  'transformation-fund',
  'pilot',
  'grant',
  'tender',
  'quality-scheme',
] as const;
export type OpportunityType = (typeof OPPORTUNITY_TYPES)[number];

export const OPPORTUNITY_TYPE_LABELS: Record<OpportunityType, string> = {
  'enhanced-service': 'Enhanced service',
  'transformation-fund': 'Transformation fund',
  'pilot': 'Pilot',
  'grant': 'Grant',
  'tender': 'Tender',
  'quality-scheme': 'Quality scheme',
};

export const OPPORTUNITY_STATUS = ['open', 'closing-soon', 'closed', 'expression-of-interest'] as const;
export type OpportunityStatus = (typeof OPPORTUNITY_STATUS)[number];

export interface MatchInput {
  /** Practice or PCN profile. */
  listSize: number;
  isPcn: boolean;
  practiceCount: number;
  icb: string | null;
  servicesOffered: string[];
  /** Types already extracted from contracts this org holds. */
  heldContractTags: string[];
}

export interface OpportunityCriteria {
  minListSize: number | null;
  maxListSize: number | null;
  pcnOnly: boolean;
  icbs: string[];
  tags: string[];
}

export interface MatchResult {
  /** 0-100. A relevance hint. Never an eligibility determination. */
  score: number;
  reasons: string[];
  /** Criteria we could not evaluate from the org profile. */
  unknowns: string[];
}

/**
 * Transparent, explainable scoring. Deliberately not a model call — a buyer
 * asking "why did this appear?" deserves an answer that is the same every time.
 */
export function scoreMatch(profile: MatchInput, criteria: OpportunityCriteria): MatchResult {
  const reasons: string[] = [];
  const unknowns: string[] = [];
  let score = 50; // neutral baseline

  if (criteria.pcnOnly) {
    if (profile.isPcn) {
      score += 15;
      reasons.push('Open to PCNs, and you are a PCN');
    } else {
      score -= 40;
      reasons.push('Open to PCNs only');
    }
  }

  if (criteria.minListSize !== null || criteria.maxListSize !== null) {
    const above = criteria.minListSize === null || profile.listSize >= criteria.minListSize;
    const below = criteria.maxListSize === null || profile.listSize <= criteria.maxListSize;
    if (above && below) {
      score += 12;
      reasons.push('Your list size falls inside the stated range');
    } else {
      score -= 30;
      reasons.push('Your list size falls outside the stated range');
    }
  }

  if (criteria.icbs.length) {
    if (!profile.icb) {
      unknowns.push('Your ICB is not set, so the geographic criterion could not be checked');
    } else if (criteria.icbs.includes(profile.icb)) {
      score += 20;
      reasons.push('Available in your ICB');
    } else {
      score -= 45;
      reasons.push('Not available in your ICB');
    }
  }

  if (criteria.tags.length) {
    const held = new Set([...profile.servicesOffered, ...profile.heldContractTags].map((t) => t.toLowerCase()));
    const overlap = criteria.tags.filter((t) => held.has(t.toLowerCase()));
    if (overlap.length) {
      score += Math.min(20, overlap.length * 8);
      reasons.push(`Relates to work you already do: ${overlap.join(', ')}`);
    } else {
      unknowns.push('No overlap found with services recorded on your profile — you may still be interested');
    }
  }

  return { score: Math.max(0, Math.min(100, score)), reasons, unknowns };
}

/**
 * Shown next to every match, everywhere, without exception.
 */
export const FUNDING_DISCLAIMER =
  'Match scores indicate how closely an opportunity fits the profile you have given us. They are not an assessment of eligibility and they do not predict the outcome of an application. Always read the published criteria before applying.';

export const FUNDING_PROVENANCE_NOTE =
  'Opportunities are added and verified by a person. Each record shows who checked it and when. InBrief does not connect to a live funding feed.';

// ---------------------------------------------------------------------------
// Assisted ingestion
// ---------------------------------------------------------------------------

/**
 * A model-written draft of an opportunity.
 *
 * PRODUCTION-BUILD.md 9 draws the line: "Automate the summarisation, not the
 * decision." This type is the summarisation. It is deliberately not the same
 * shape as the published record — it has no `verifiedBy`, no `verifiedAt` and
 * no `publishedAt`, because those three fields are the decision and a model
 * must have no way to write them. Publishing is a separate act, by a named
 * person, in curate.ts.
 *
 * Every field a model could not find in the source is null rather than
 * guessed. A curator filling a blank is doing the job; a curator failing to
 * notice a plausible invention is the failure this whole design is arranged
 * to prevent.
 */
export const fundingDraftSchema = z.object({
  title: z.string().min(6).max(200),
  funder: z.string().min(2).max(200),
  type: z.enum(OPPORTUNITY_TYPES),
  status: z.enum(OPPORTUNITY_STATUS),
  summary: z.string().min(20).max(1200),
  valueDescription: z.string().max(300).nullable(),
  /** As written in the source. Not normalised into a date — see below. */
  closesAtText: z.string().max(120).nullable(),
  criteria: z.object({
    minListSize: z.number().int().positive().nullable(),
    maxListSize: z.number().int().positive().nullable(),
    pcnOnly: z.boolean(),
    icbs: z.array(z.string().max(200)).max(50),
    tags: z.array(z.string().max(100)).max(50),
  }),
  /** Where in the source each non-obvious field came from, for the curator. */
  sourceNotes: z.array(z.string().max(500)).max(30),
  /** What the model could not determine. Drives the publish checklist. */
  unknowns: z.array(z.string().max(300)).max(30),
});

export type FundingDraft = z.infer<typeof fundingDraftSchema>;

/**
 * The JSON Schema handed to `output_config.format`.
 *
 * Hand-written rather than generated, for the same reason as the extraction
 * schema: the descriptions are instruction to the model and they carry most of
 * the accuracy.
 *
 * Note `closesAtText` is a string, not a date. A closing date in an ICB board
 * paper is routinely "the end of Q3", "four weeks from publication" or "31
 * March" with no year, and a model asked for an ISO date will produce one —
 * confidently, and wrong. The curator converts it, because the curator can
 * tell which March.
 */
export const FUNDING_DRAFT_JSON_SCHEMA = {
  type: 'object',
  properties: {
    title: {
      type: 'string',
      description: 'The name of the opportunity as the source calls it. Not a description of it.',
    },
    funder: {
      type: 'string',
      description:
        'The organisation offering the money, exactly as named in the source. If the source names a programme but not the body behind it, give the programme.',
    },
    type: {
      type: 'string',
      enum: [...OPPORTUNITY_TYPES],
      description:
        'enhanced-service: a locally commissioned service with a payment per activity. transformation-fund: time-limited money for change. pilot: a time-limited trial. grant: an application for a fixed sum. tender: a competitive procurement. quality-scheme: payment against quality measures.',
    },
    status: {
      type: 'string',
      enum: [...OPPORTUNITY_STATUS],
      description:
        'Only from what the source says. If the source gives no closing date and no indication it has closed, use "open". Use "expression-of-interest" where the first step is an EOI rather than a full application.',
    },
    summary: {
      type: 'string',
      description:
        'Two to four sentences: what the money is for and what a provider would have to do to get it. Plain English, factual, drawn from the source. Do not add encouragement, do not describe it as a good fit, and do not state or imply that any particular organisation is eligible.',
    },
    valueDescription: {
      type: ['string', 'null'],
      description:
        'The money, as written — "£28 per completed review", "up to £15,000", "analyst time, not cash". Null if the source does not say. Never estimate a value.',
    },
    closesAtText: {
      type: ['string', 'null'],
      description:
        'The deadline exactly as the source words it, including a vague one such as "end of Q3" or a date with no year. Do not convert it to a calendar date and do not infer a missing year. Null if the source gives no deadline.',
    },
    criteria: {
      type: 'object',
      description:
        'Only criteria the source states outright. These drive a relevance score, so an invented criterion silently hides real opportunities from the practices it excludes.',
      properties: {
        minListSize: {
          type: ['integer', 'null'],
          description: 'Minimum registered population, if the source states one. Null otherwise.',
        },
        maxListSize: {
          type: ['integer', 'null'],
          description: 'Maximum registered population, if the source states one. Null otherwise.',
        },
        pcnOnly: {
          type: 'boolean',
          description:
            'True only where the source says the applicant must be a PCN. False if it is open to individual practices, and false if the source is silent — silence is not a restriction.',
        },
        icbs: {
          type: 'array',
          items: { type: 'string' },
          description:
            'ICBs the source names as in scope, written as the source writes them. Empty if the source names none — do not infer a geography from the funder\'s name.',
        },
        tags: {
          type: 'array',
          items: { type: 'string' },
          description:
            'Short subject labels for the clinical or operational area, e.g. "diabetes", "health inequalities", "medicines optimisation". Two to five is usually right.',
        },
      },
      required: ['minListSize', 'maxListSize', 'pcnOnly', 'icbs', 'tags'],
      additionalProperties: false,
    },
    sourceNotes: {
      type: 'array',
      items: { type: 'string' },
      description:
        'For each field that was not obvious, where in the source you found it — a page, a heading, or a short quote. This is what the curator checks you against, so it matters more than the summary.',
    },
    unknowns: {
      type: 'array',
      items: { type: 'string' },
      description:
        'Everything a practice would need to know that this source does not say — the deadline, the value, who may apply, how to apply. List them plainly. An honest gap here becomes a question the curator answers before publishing; a gap you paper over becomes a published record that is wrong.',
    },
  },
  required: [
    'title', 'funder', 'type', 'status', 'summary', 'valueDescription',
    'closesAtText', 'criteria', 'sourceNotes', 'unknowns',
  ],
  additionalProperties: false,
} as const;

/**
 * Language that asserts eligibility or predicts success.
 *
 * "Never assert eligibility or likely success. This is both a legal and a
 * reputational line" — PRODUCTION-BUILD.md 9. The prompt says so too, and this
 * is the backstop for when the prompt is not enough. A practice that applies
 * because InBrief told it it qualified, and is turned down, has been harmed by
 * this product in a way no disclaimer at the bottom of the page undoes.
 *
 * Matching is on the published prose only — summary, title, value. Curator
 * notes and the unknowns list are exempt, because "unclear whether we are
 * eligible" is exactly what a curator should be allowed to write down.
 */
const ELIGIBILITY_ASSERTIONS: { pattern: RegExp; why: string }[] = [
  {
    pattern: /\byou (are|would be|will be) eligible\b/i,
    why: 'states the reader is eligible',
  },
  {
    pattern: /\byour (practice|pcn|organisation) (is|would be|will be) eligible\b/i,
    why: 'states the reader is eligible',
  },
  {
    pattern: /\b(you|your practice|your pcn) (qualify|qualifies|would qualify)\b/i,
    why: 'states the reader qualifies',
  },
  {
    pattern: /\b(guaranteed|guarantee[sd]?) (funding|payment|award|success)\b/i,
    why: 'guarantees an outcome',
  },
  {
    pattern: /\b(likely|certain|sure) to (succeed|be awarded|win|be approved)\b/i,
    why: 'predicts the outcome of an application',
  },
  {
    pattern: /\b(strong|excellent|good) chance of (success|being awarded|winning)\b/i,
    why: 'predicts the outcome of an application',
  },
  {
    pattern: /\bshould apply\b/i,
    why: 'advises the reader to apply rather than describing the opportunity',
  },
  {
    pattern: /\bwe recommend applying\b/i,
    why: 'advises the reader to apply rather than describing the opportunity',
  },
];

export interface EligibilityFinding {
  field: string;
  matched: string;
  why: string;
}

export function findEligibilityAssertions(draft: {
  title: string;
  summary: string;
  valueDescription: string | null;
}): EligibilityFinding[] {
  const findings: EligibilityFinding[] = [];
  const fields: [string, string | null][] = [
    ['title', draft.title],
    ['summary', draft.summary],
    ['valueDescription', draft.valueDescription],
  ];

  for (const [field, text] of fields) {
    if (!text) continue;
    for (const { pattern, why } of ELIGIBILITY_ASSERTIONS) {
      const match = text.match(pattern);
      if (match) findings.push({ field, matched: match[0], why });
    }
  }

  return findings;
}

/**
 * What still stands between a draft and publication.
 *
 * Returns an empty array when the record is publishable. Everything here is a
 * hard block rather than a warning, because the alternative — a publish
 * command that prints warnings and publishes anyway — is a publish command
 * with no checks at all.
 */
export interface PublishCandidate {
  title: string;
  funder: string;
  type: string;
  status: string;
  summary: string;
  valueDescription: string | null;
  sourceDescription: string | null;
  sourceUrl: string | null;
  closesAt: Date | string | null;
  verifiedBy: string | null;
  criteria: Partial<OpportunityCriteria> | null;
  isExample: boolean;
}

export function publishBlockers(candidate: PublishCandidate): string[] {
  const blockers: string[] = [];

  if (!candidate.verifiedBy || candidate.verifiedBy.trim().length < 2) {
    blockers.push(
      'No curator named. A published opportunity carries the name of the person who checked it, and "the model drafted it" is not a name.',
    );
  }

  if (!candidate.sourceDescription || candidate.sourceDescription.trim().length < 10) {
    blockers.push(
      'No source described. A reader needs to know what this was taken from — an ICB board paper, a tender notice, an email — well enough to go and read it.',
    );
  }

  if (!OPPORTUNITY_TYPES.includes(candidate.type as OpportunityType)) {
    blockers.push(`Type "${candidate.type}" is not one of: ${OPPORTUNITY_TYPES.join(', ')}.`);
  }

  if (!OPPORTUNITY_STATUS.includes(candidate.status as OpportunityStatus)) {
    blockers.push(`Status "${candidate.status}" is not one of: ${OPPORTUNITY_STATUS.join(', ')}.`);
  }

  // A status that implies a deadline, without a deadline, produces a card that
  // says "closing soon" next to nothing at all.
  if ((candidate.status === 'closing-soon' || candidate.status === 'open') && !candidate.closesAt) {
    blockers.push(
      `Status is "${candidate.status}" but no closing date is set. Either set the date, or use a status that does not imply one.`,
    );
  }

  if (candidate.summary.trim().length < 20) {
    blockers.push('Summary is too short to be useful to a manager deciding whether to read further.');
  }

  const assertions = findEligibilityAssertions(candidate);
  for (const finding of assertions) {
    blockers.push(
      `The ${finding.field} ${finding.why} ("${finding.matched}"). Rewrite it to describe the opportunity, not the reader's prospects.`,
    );
  }

  const criteria = candidate.criteria ?? {};
  if (
    criteria.minListSize != null &&
    criteria.maxListSize != null &&
    criteria.minListSize > criteria.maxListSize
  ) {
    blockers.push(
      `List size range is inverted (minimum ${criteria.minListSize} is above maximum ${criteria.maxListSize}), which matches no practice at all.`,
    );
  }

  return blockers;
}
